import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/common/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useMyListings, useListings } from '@/features/mercado/api';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { formatClp } from '@/lib/format/currency';
import { cn } from '@/lib/utils/cn';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import type { Listing } from '@condo/contracts';

const STATUS_LABEL: Record<string, string> = {
  active: 'Activo',
  sold: 'Vendido',
  draft: 'Borrador',
  removed: 'Eliminado',
};

const STATUS_VARIANT: Record<string, 'success' | 'neutral' | 'warning' | 'danger'> = {
  active: 'success',
  sold: 'neutral',
  draft: 'warning',
  removed: 'danger',
};

function ListingRow({ listing, onClick }: { listing: Listing; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-4 rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-4',
        'transition-all duration-150 hover:border-[var(--color-hairline-strong)] hover:bg-[var(--color-paper-dim)]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
        'text-left',
      )}
    >
      {listing.photos[0] ? (
        <img
          src={listing.photos[0].url}
          alt=""
          className="h-16 w-16 shrink-0 rounded object-cover"
        />
      ) : (
        <div className="h-16 w-16 shrink-0 rounded bg-[var(--color-paper-dim)]" />
      )}
      <div className="flex-1 overflow-hidden">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-semibold text-[var(--color-ink)]">
            {listing.title}
          </p>
          <Badge variant={STATUS_VARIANT[listing.status] ?? 'neutral'}>
            {STATUS_LABEL[listing.status] ?? listing.status}
          </Badge>
        </div>
        <p className="mt-0.5 font-display text-lg font-semibold tracking-tight text-[var(--color-ink)] tabular">
          {listing.priceCLP === 0 ? 'Gratis' : formatClp(listing.priceCLP)}
        </p>
        <p className="mt-0.5 text-xs text-[var(--color-ink-faint)]">
          {listing.sellerName} · {new Date(listing.createdAt).toLocaleDateString('es-CL')}
        </p>
      </div>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Admin view — all condo listings                                            */
/* -------------------------------------------------------------------------- */

function AdminListingsView() {
  const navigate = useNavigate();
  const { condoId: tenantId } = useCondoContext();

  const { data, isLoading } = useListings();
  const listings = (data?.items ?? []).filter(
    (l) => l.sellerTenantId === tenantId,
  );

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Comunidad · Mercado Vecinal"
        title="Avisos del condominio"
        subtitle="Publicaciones activas de los residentes de tu comunidad."
      />

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-md" />
          ))}
        </div>
      ) : listings.length === 0 ? (
        <EmptyState
          title="Sin publicaciones activas"
          description="Los residentes del condominio aun no tienen articulos publicados."
        />
      ) : (
        <div className="space-y-3">
          {listings.map((listing) => (
            <ListingRow
              key={listing.id}
              listing={listing}
              onClick={() => navigate(`/app/condominios/${tenantId}/mercado/${listing.id}`)}
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

/* -------------------------------------------------------------------------- */
/* Resident view — own listings                                               */
/* -------------------------------------------------------------------------- */

function ResidentListingsView() {
  const navigate = useNavigate();
  const { condoId } = useCondoContext();
  const { data, isLoading } = useMyListings();
  const listings = data?.items ?? [];

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Comunidad · Mercado Vecinal"
        title="Mis publicaciones"
        subtitle="Administra tus avisos activos, vendidos y borradores."
        action={
          <Button
            variant="accent"
            onClick={() => navigate(`/app/condominios/${condoId}/mercado/publicar`)}
          >
            <Plus className="h-4 w-4" />
            Nuevo aviso
          </Button>
        }
      />

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-md" />
          ))}
        </div>
      ) : listings.length === 0 ? (
        <EmptyState
          title="Sin publicaciones"
          description="Publica tu primer articulo y empieza a vender entre vecinos."
          action={
            <Button
              variant="accent"
              onClick={() => navigate(`/app/condominios/${condoId}/mercado/publicar`)}
            >
              <Plus className="h-4 w-4" />
              Publicar aviso
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {listings.map((listing) => (
            <ListingRow
              key={listing.id}
              listing={listing}
              onClick={() => navigate(`/app/condominios/${condoId}/mercado/${listing.id}`)}
            />
          ))}
        </div>
      )}
    </PageContainer>
  );
}

/* -------------------------------------------------------------------------- */
/* Route entry — dispatch by role                                              */
/* -------------------------------------------------------------------------- */

export function MisPublicacionesRoute() {
  const { rol } = useCondoContext();
  const isAdmin = rol === 'admin' || rol === 'comite' || rol === 'super_admin';
  return isAdmin ? <AdminListingsView /> : <ResidentListingsView />;
}